import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Flex, Text, Select, Button, Spinner, FormLabel, FormControl, Input,
  Card, CardBody,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

interface AdminUserFormProps {
  initialData?: { fullName: string; email: string; role: string };
  userId?: string;
}

export function AdminUserForm({ initialData, userId: userIdProp }: AdminUserFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: routeId } = useParams<{ id: string }>();
  const userId = userIdProp ?? routeId;
  const isEdit = Boolean(userId);

  const textColor = useColorModeValue('navy.700', 'white');
  const selectBg = useColorModeValue('secondaryGray.300', 'navy.900');

  // Fetch existing user data when editing via route param
  const { data: fetchedUser, isLoading: isFetching } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => apiClient.get<{ fullName: string; email: string; role: string }>(`/admin/admin-users/${userId}`),
    enabled: isEdit && !initialData,
  });

  const resolved = initialData ?? (fetchedUser ? {
    fullName: fetchedUser.fullName,
    email: fetchedUser.email,
    role: fetchedUser.role,
  } : undefined);

  const [fullName, setFullName] = useState(resolved?.fullName ?? '');
  const [email, setEmail] = useState(resolved?.email ?? '');
  const [role, setRole] = useState(resolved?.role ?? 'support');
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [fullNameError, setFullNameError] = useState<string | null>(null);

  // Populate form fields once data arrives
  useEffect(() => {
    if (resolved) {
      setFullName(resolved.fullName);
      setEmail(resolved.email);
      setRole(resolved.role);
    }
    // resolved is rebuilt each render; depend on its primitive fields, not its identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved?.fullName, resolved?.email, resolved?.role]);

  const mutation = useMutation({
    mutationFn: async () => {
      let hasError = false;
      if (!fullName.trim()) {
        setFullNameError(t('validation.nameRequired', { field: t('admin.users.fullName') }));
        hasError = true;
      }
      if (!isEdit) {
        if (!email.trim()) {
          setEmailError(t('validation.emailRequired'));
          hasError = true;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
          setEmailError(t('validation.emailInvalid'));
          hasError = true;
        }
      }
      if (hasError) throw new Error('Validation failed');
      if (isEdit) {
        return apiClient.patch(`/admin/admin-users/${userId}`, { fullName, role });
      }
      return apiClient.post('/admin/admin-users', { email, fullName, role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      navigate('/admin-users');
    },
    onError: (err: Error) => setError(err.message),
  });

  const roles = [
    { value: 'support', label: t('admin.users.roles.support') },
    { value: 'content_curator', label: t('admin.users.roles.contentCurator') },
    { value: 'system_admin', label: t('admin.users.roles.systemAdmin') },
  ];

  if (isEdit && isFetching) {
    return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="admin-user-form">
      <Card maxW="500px">
        <CardBody>

        {error && (
          <Text color="red.500" mb="16px" data-testid="form-error">
            {error}
          </Text>
        )}

        {!isEdit && (
          <FormControl mb="24px" isRequired isInvalid={!!emailError}>
            <FormLabel ms="10px" fontSize="sm" color={textColor} fontWeight="bold">
              {t('admin.users.email')}
            </FormLabel>
            <Input
              id="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
              placeholder="admin@streamko.com"
              borderRadius="16px"
              h="44px"
              data-testid="input-email"
            />
            {emailError && <Text color="red.500" fontSize="sm" mt={1}>{emailError}</Text>}
          </FormControl>
        )}

        <FormControl mb="24px" isRequired isInvalid={!!fullNameError}>
          <FormLabel ms="10px" fontSize="sm" color={textColor} fontWeight="bold">
            {t('admin.users.fullName')}
          </FormLabel>
          <Input
            id="fullName"
            value={fullName}
            onChange={(e) => { setFullName(e.target.value); setFullNameError(null); }}
            placeholder="Full Name"
            borderRadius="16px"
            h="44px"
            data-testid="input-fullname"
          />
          {fullNameError && <Text color="red.500" fontSize="sm" mt={1}>{fullNameError}</Text>}
        </FormControl>

        <Box mb="24px">
          <FormLabel
            ms="10px"
            fontSize="sm"
            color={textColor}
            fontWeight="bold"
          >
            {t('admin.users.role')}
          </FormLabel>
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            borderRadius="16px"
            bg={selectBg}
            color={textColor}
            h="44px"
            data-testid="select-role"
          >
            {roles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </Box>

        <Button
          variant="brand"
          w="100%"
          h="44px"
          onClick={() => mutation.mutate()}
          isDisabled={mutation.isPending}
          data-testid="btn-submit"
        >
          {mutation.isPending ? <Spinner size="sm" /> : (isEdit ? t('common.save') : t('admin.users.create'))}
        </Button>
        </CardBody>
      </Card>
    </Box>
  );
}
